import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import adminApi from '../services/adminApi';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [adminExists, setAdminExists] = useState(true);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      // If already logged in, redirect to dashboard
      if (adminApi.isAuthenticated()) {
        const valid = await adminApi.verifyToken();
        if (valid) {
          navigate('/admin/dashboard');
          return;
        }
      }
      
      // Check if admin exists
      try {
        const { admin_exists } = await adminApi.checkAdminExists();
        setAdminExists(admin_exists);
        setIsRegister(!admin_exists);
      } catch (err) {
        console.error('Error checking admin status:', err);
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      if (isRegister) {
        await adminApi.register(formData.username, formData.password, formData.email);
      } else {
        await adminApi.login(formData.username, formData.password);
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">
            {isRegister ? 'Create Admin Account' : 'Admin Login'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {isRegister 
              ? 'Set up your admin account to manage the gallery'
              : 'Sign in to manage your inventory'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Enter username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  required
                  minLength={3}
                  data-testid="admin-username-input"
                />
              </div>
            </div>
            
            {isRegister && (
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  required
                  minLength={6}
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-teal-600 hover:bg-teal-500 text-white"
              disabled={submitting}
              data-testid="admin-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isRegister ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                isRegister ? 'Create Account' : 'Sign In'
              )}
            </Button>
            
            {adminExists && (
              <p className="text-center text-sm text-gray-400">
                {isRegister ? (
                  <>
                    Already have an account?{' '}
                    <button 
                      type="button"
                      onClick={() => setIsRegister(false)}
                      className="text-teal-400 hover:text-teal-300"
                    >
                      Sign in
                    </button>
                  </>
                ) : (
                  <>
                    Need to create an account?{' '}
                    <span className="text-gray-500">Contact existing admin</span>
                  </>
                )}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
